// Read view of one knowledge-base page (design §10): path breadcrumb,
// title, applies_to and tags, who last changed it (a person, or an agent
// with a link to its run), the "may be out of date" notice with the changed
// files and a "Still accurate" action (§8), and the rendered Markdown.

import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { Markdown, type ResolvedRelativeLink } from '../markdown'
import { formatDateTime, formatRelative, shortId } from '../../lib/format'
import { kbAuthorName, type KbAuthor, type KbPage } from './kb-types'

type AuthorLike = Partial<KbAuthor> & { runLabel?: string | null }

export interface KbPageViewProps {
  page: KbPage
  /** Label of the page's space, first breadcrumb segment (e.g. "Workspace", "Goblic"). */
  spaceLabel: string
  /** Breadcrumb folder click (prefix with trailing slash). */
  onSelectFolder?: (folder: string) => void
  onEdit?: () => void
  onShowHistory?: () => void
  onDelete?: () => void
  /** Clears the stale flag and sets verified_at (§8). */
  onMarkVerified?: () => void
  markingVerified?: boolean
  /** Extra header actions (e.g. a ⋯ menu). */
  actions?: ReactNode
  /**
   * Who made the current revision, with names (from the revision list). The
   * page itself only carries ids; without this the line says "A person" /
   * "An agent".
   */
  updatedBy?: AuthorLike | null
  /** For a deleted page (opened with includeDeleted): bring it back. */
  onRestoreDeleted?: () => void
  restoringDeleted?: boolean
  /** Relative Markdown links (`../areas/api.md`): where they go, or null (shown as text). */
  resolveRelativeLink?: (href: string) => ResolvedRelativeLink | null
}

export function KbPageView({
  page,
  spaceLabel,
  onSelectFolder,
  onEdit,
  onShowHistory,
  onDelete,
  onMarkVerified,
  markingVerified,
  actions,
  updatedBy,
  onRestoreDeleted,
  restoringDeleted,
  resolveRelativeLink,
}: KbPageViewProps) {
  const author: AuthorLike = updatedBy ?? {
    userId: page.updatedByUserId,
    runId: page.updatedByRunId,
  }
  const stale = page.stale
  const segments = page.path.split('/')
  const appliesTo = page.frontmatter.applies_to ?? []
  const tags = page.frontmatter.tags ?? []

  return (
    <article className="min-w-0">
      <nav aria-label="Path" className="mb-2 flex flex-wrap items-center gap-1 text-sm text-ink-3">
        <span>{spaceLabel}</span>
        {segments.map((segment, index) => {
          const last = index === segments.length - 1
          const prefix = `${segments.slice(0, index + 1).join('/')}/`
          return (
            <span key={index} className="flex items-center gap-1">
              <span aria-hidden>/</span>
              {last ? (
                <span className="font-mono text-ink-2">{segment}</span>
              ) : onSelectFolder ? (
                <button
                  type="button"
                  onClick={() => onSelectFolder(prefix)}
                  className="hover:text-ink hover:underline"
                >
                  {segment}
                </button>
              ) : (
                <span>{segment}</span>
              )}
            </span>
          )
        })}
      </nav>

      <header className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <h1 className="font-display text-xl font-semibold tracking-[-0.01em] text-ink">
            {page.title}
          </h1>
          {page.summary ? (
            <p className="mt-1 max-w-2xl text-base text-ink-2">{page.summary}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {onShowHistory ? (
            <button type="button" className="btn-ghost btn-sm" onClick={onShowHistory}>
              History
            </button>
          ) : null}
          {onDelete ? (
            <button type="button" className="btn-danger-quiet btn-sm" onClick={onDelete}>
              Delete
            </button>
          ) : null}
          {onEdit ? (
            <button type="button" className="btn-primary btn-sm" onClick={onEdit}>
              Edit
            </button>
          ) : null}
          {actions}
        </div>
      </header>

      <dl className="mt-3 space-y-1.5 text-sm">
        {appliesTo.length ? (
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <dt className="text-ink-3">Applies to</dt>
            <dd className="flex flex-wrap gap-1">
              {appliesTo.map((glob) => (
                <code key={glob} className="rounded bg-sunken px-1.5 py-0.5 font-mono text-xs text-ink-2">
                  {glob}
                </code>
              ))}
            </dd>
          </div>
        ) : null}
        {tags.length ? (
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <dt className="text-ink-3">Tags</dt>
            <dd className="flex flex-wrap gap-1">
              {tags.map((tag) => (
                <span key={tag} className="rounded-full bg-moss-soft px-2 text-xs font-medium text-moss">
                  {tag}
                </span>
              ))}
            </dd>
          </div>
        ) : null}
        <div className="flex flex-wrap items-baseline gap-x-2">
          <dt className="text-ink-3">Last changed</dt>
          <dd className="text-ink-2">
            <KbAuthorLine author={author} at={page.updatedAt} />
            <span className="text-ink-3"> · rev {page.rev}</span>
            {page.frontmatter.always_load ? (
              <span className="ml-2 rounded-full bg-info-soft px-2 text-xs font-medium text-info">
                Always loaded
              </span>
            ) : null}
          </dd>
        </div>
      </dl>

      {page.deleted ? (
        <div
          role="status"
          className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-control border border-danger/30 bg-danger-soft px-4 py-3 text-base text-ink"
        >
          <span>
            <span className="font-medium">This page was deleted.</span>{' '}
            <span className="text-sm text-ink-2">
              It can be restored for 30 days after deletion.
            </span>
          </span>
          {onRestoreDeleted ? (
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={onRestoreDeleted}
              disabled={restoringDeleted}
            >
              {restoringDeleted ? 'Restoring…' : 'Restore page'}
            </button>
          ) : null}
        </div>
      ) : null}

      {stale ? (
        <div
          role="status"
          className="mt-4 rounded-control border border-signal/30 bg-signal-soft px-4 py-3 text-base text-ink"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium">May be out of date</p>
              <p className="mt-0.5 text-sm text-ink-2">
                {stale.runId ? (
                  <>
                    <RunLink runId={stale.runId} />{' '}
                    changed files this page applies to
                  </>
                ) : (
                  'Files this page applies to changed'
                )}{' '}
                <span title={formatDateTime(stale.since)}>
                  {formatRelative(stale.since)}
                </span>
                .
              </p>
              {stale.files.length ? (
                <ul className="mt-1.5 flex flex-wrap gap-1">
                  {stale.files.map((file) => (
                    <li key={file}>
                      <code className="rounded bg-surface/70 px-1.5 py-0.5 font-mono text-xs text-ink-2">
                        {file}
                      </code>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
            {onMarkVerified ? (
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={onMarkVerified}
                disabled={markingVerified}
              >
                {markingVerified ? 'Saving…' : 'Still accurate'}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="mt-5 border-t border-line pt-5 text-base leading-relaxed text-ink">
        {page.content.trim() ? (
          <div className="max-w-3xl">
            <Markdown variant="page" resolveRelativeLink={resolveRelativeLink}>
              {page.content}
            </Markdown>
          </div>
        ) : (
          <p className="text-ink-3">This page is empty.</p>
        )}
      </div>
    </article>
  )
}

/** Link to a run; shows `label` (e.g. "#482") or a short id. */
export function RunLink({ runId, label }: { runId: string; label?: string | null }) {
  return (
    <Link to="/runs/$runId" params={{ runId }} className="link">
      run {label ?? shortId(runId)}
    </Link>
  )
}

/** "Agent Developer · run #482 · 2 hours ago" or "Paul · 2 hours ago". */
export function KbAuthorLine({ author, at }: { author: AuthorLike; at?: string | null }) {
  return (
    <span>
      {kbAuthorName(author)}
      {author.runId ? (
        <>
          {' · '}
          <RunLink runId={author.runId} label={author.runLabel} />
        </>
      ) : null}
      {at ? (
        <>
          {' · '}
          <span title={formatDateTime(at)}>{formatRelative(at)}</span>
        </>
      ) : null}
    </span>
  )
}
